// @flow

import moment from 'moment';

import type {StoreShapeType} from 'app/reducers/types';

import {
  buildPresenceMessageContent,
  getCurrentUserAndAccountIds,
  setBeaconInterval,
  unsetBeaconInterval,
  setPresenceMemberTimeout,
  unsetAllPresenceMembersTimeout,
  unsetPresenceMemberTimeout,
  shouldUpdateMemberSubscription,
} from './utils';

import type {
  SubscriptionDataType,
  PresenceMessageDataType,
  PresenceMessageChannelDataType,
  StoredPresenceMessageChannelDataType,
} from './types';

export type PresenceActions =
  | PresenceSubscribeActionType
  | PresenceUnsubscribeActionType
  | ReceivePresenceMemberActionType
  | RemovePresenceMemberActionType
  | ReceivePresenceBeaconActionType
  | ReceivePresenceAttendActionType;

export type CommonPresenceActionParams = {|
  ...SubscriptionDataType,
  sendWebsocketMessage: (string) => void,
|};

function genericReceiveMemberPresence(
  /* generic function used to receive both join and beacon messages */
  event: string,
  data: PresenceMessageDataType,
  getState: () => StoreShapeType,
  dispatch: SimpleDispatch,
  actionType:
    | 'RECEIVE_PRESENCE_MEMBER'
    | 'RECEIVE_PRESENCE_BEACON'
    | 'RECEIVE_PRESENCE_ATTEND',
  additionalAction?: (StoredPresenceMessageChannelDataType) => void
) {
  if (shouldUpdateMemberSubscription(event, data, getState)) {
    const memberPresenceTimeoutID = setPresenceMemberTimeout(
      data,
      getState,
      (data) => ((dispatch: any): FuncDispatch)(removePresenceMember(data))
    );

    dispatch({
      type: actionType,
      memberPresenceTimeoutID,
      data,
    });
  }

  const currentSubscription = getState().presence.getIn([
    'subscriptions',
    data.group,
  ]);
  if (additionalAction != null && currentSubscription != null) {
    additionalAction(currentSubscription);
  }
}

/* subscribe */

export const PRESENCE_SUBSCRIBE = 'PRESENCE_SUBSCRIBE';

export type PresenceSubscribeActionType = {
  type: 'PRESENCE_SUBSCRIBE',
  groups: Array<{
    name: string,
    beaconInterval: number,
    beaconIntervalID: null | IntervalID,
    groupData: PresenceMessageChannelDataType,
  }>,
};

export const subscribeForPresence =
  (
    {
      groups,
      context,
      sendWebsocketMessage,
      additionalData,
    }: CommonPresenceActionParams,
    {
      beaconInterval = 60,
      stealth = false,
    }: {
      beaconInterval?: number,
      stealth?: boolean,
    }
  ) =>
  (dispatch: SimpleDispatch, getState: () => StoreShapeType) => {
    const {user_id, account_id} = getCurrentUserAndAccountIds(getState);

    const groupData = {
      user_id,
      account_id,
      beaconInterval,
      context,
      additionalData,
      joinedAt: moment.utc().valueOf(),
    };

    dispatch(
      ({
        type: PRESENCE_SUBSCRIBE,
        groups: groups.map((name) => ({
          name,
          stealth,
          beaconInterval,
          beaconIntervalID: stealth
            ? null
            : setBeaconInterval(
                name,
                beaconInterval,
                getState,
                sendWebsocketMessage
              ),
          groupData,
        })),
      }: PresenceSubscribeActionType)
    );

    sendWebsocketMessage(
      buildPresenceMessageContent(
        stealth ? 'stealth_join' : 'join',
        groups,
        getState()
      )
    );
  };

/* unsubscribe */

export const PRESENCE_UNSUBSCRIBE = 'PRESENCE_UNSUBSCRIBE';

type PresenceUnsubscribeActionType = {
  type: 'PRESENCE_UNSUBSCRIBE',
  groups: Array<string>,
};

export const unsubscribeForPresence =
  ({groups, sendWebsocketMessage}: CommonPresenceActionParams) =>
  (dispatch: SimpleDispatch, getState: () => StoreShapeType) => {
    groups.forEach((group) => {
      unsetBeaconInterval(group, getState);
      unsetAllPresenceMembersTimeout(group, getState);
    });

    dispatch(
      ({
        type: PRESENCE_UNSUBSCRIBE,
        groups,
      }: PresenceUnsubscribeActionType)
    );

    sendWebsocketMessage(
      buildPresenceMessageContent('leave', groups, getState())
    );
  };

/* new member */

export const RECEIVE_PRESENCE_MEMBER = 'RECEIVE_PRESENCE_MEMBER';

type ReceivePresenceMemberActionType = {
  type: 'RECEIVE_PRESENCE_MEMBER',
  memberPresenceTimeoutID: TimeoutID,
  data: PresenceMessageDataType,
};

export const receivePresenceMember =
  (
    event: string,
    data: PresenceMessageDataType,
    sendWebsocketMessage: (string) => void
  ) =>
  (dispatch: FuncDispatch, getState: () => StoreShapeType) => {
    genericReceiveMemberPresence(
      event,
      data,
      getState,
      dispatch,
      RECEIVE_PRESENCE_MEMBER,
      ({stealth}) =>
        !stealth
          ? sendWebsocketMessage(
              buildPresenceMessageContent('attend', [data.group], getState())
            )
          : undefined
    );
  };

/* member exit */

export const REMOVE_PRESENCE_MEMBER = 'REMOVE_PRESENCE_MEMBER';

type RemovePresenceMemberActionType = {
  type: 'REMOVE_PRESENCE_MEMBER',
  data: PresenceMessageDataType,
};

export const removePresenceMember =
  (data: PresenceMessageDataType) =>
  (dispatch: SimpleDispatch, getState: () => StoreShapeType) => {
    dispatch(
      ({
        type: REMOVE_PRESENCE_MEMBER,
        data,
      }: RemovePresenceMemberActionType)
    );
    unsetPresenceMemberTimeout(data, getState);
  };

/* receive beacon */

export const RECEIVE_PRESENCE_BEACON = 'RECEIVE_PRESENCE_BEACON';

type ReceivePresenceBeaconActionType = {
  type: 'RECEIVE_PRESENCE_BEACON',
  memberPresenceTimeoutID: TimeoutID,
  data: PresenceMessageDataType,
};

export const receivePresenceBeacon =
  (data: PresenceMessageDataType) =>
  (dispatch: FuncDispatch, getState: () => StoreShapeType) => {
    genericReceiveMemberPresence(
      'beacon',
      data,
      getState,
      dispatch,
      RECEIVE_PRESENCE_BEACON
    );
  };

/* receive presence attend */

export const RECEIVE_PRESENCE_ATTEND = 'RECEIVE_PRESENCE_ATTEND';

type ReceivePresenceAttendActionType = {
  type: 'RECEIVE_PRESENCE_ATTEND',
  memberPresenceTimeoutID: TimeoutID,
  data: PresenceMessageDataType,
};

export const receivePresenceAttend =
  (data: PresenceMessageDataType) =>
  (dispatch: FuncDispatch, getState: () => StoreShapeType) =>
    genericReceiveMemberPresence(
      'attend',
      data,
      getState,
      dispatch,
      RECEIVE_PRESENCE_ATTEND
    );
